import { Box, Button, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material"
import { Column } from "../../../robots/components/v2/_type";
import { defaultHeaderStyle, defaultTableCellStyle } from "../../../robots/components/v2/_style";
import { useListMap } from "../../../../hooks/map";
import Fuse from "fuse.js";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { columns, fuseOptions } from "../_data";
import { COLOR } from "../../../../utils/colors";
import { useQueryState } from "nuqs";
import { MapType } from "../../../../hooks/map.type";
import { useListConfigMap } from "../../../../hooks/config-page";
import { useDebounce } from "../../../../utils/debounce";

type ColumnId = typeof columns[number]["id"];
type ColumnData = Partial<{ [key in ColumnId]: string }>

export default function LayoutMapTable({
  search,
  onUpdateData,
  onEdit,
  isDefaultMockFilled = false,
  selectable = false,
  onSelectedChange
}: {
  search?: string,
  onUpdateData?: (maps: MapType[]) => void
  onEdit?: (mapId: string) => void,
  isDefaultMockFilled?: boolean,
  selectable?: boolean,
  onSelectedChange?: (selecteds: MapType[]) => void
}) {
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedLayoutMaps, setSelectedLayoutMaps] = useState<MapType[]>([])
  const searchQuery = useDebounce(search, 300)

  const [buildingIdQuery] = useQueryState('building_uuid')

  const renderHeader = (column: Column) => {
    return {
      ...defaultHeaderStyle,
      padding: column.padding || defaultHeaderStyle.padding
    };
  };

  const { data: dataMap, refetch: refetchData } = useListConfigMap({
    building_uuid: buildingIdQuery || '',
    search: searchQuery || '',
    page,
    page_size: rowsPerPage,
  })
  
  const maps = dataMap?.data || []
  const itemCount = dataMap?.count || 1

  const filteredRobots = maps;

  const onHandleEdit = (mapId: string) => {
    onEdit?.(mapId)
  }

  const isSelected = (layoutMap: MapType) => {
    return selectedLayoutMaps.find((r) => r.map_uuid === layoutMap.map_uuid) !== undefined
  }

  const handleSelectMap = (map: MapType) => {
    if (isSelected(map)) {
      const updatedSelectedRobots = selectedLayoutMaps.filter((r) => r.map_uuid !== map.map_uuid)
      setSelectedLayoutMaps(updatedSelectedRobots)
      onSelectedChange?.(updatedSelectedRobots)
      return
    }
    const newSelectedRobots = [...selectedLayoutMaps, map]
    setSelectedLayoutMaps(newSelectedRobots)
    onSelectedChange?.(newSelectedRobots)
  }

  const rowsToShow = filteredRobots.map((data) => ({
    preview: (data.map_image ? <img src={data.map_image} style={{
      width: "265px",
      height: "233px",
      objectFit: 'scale-down',
    }} /> : <span>No Image</span>),
    ...data,
    action: (
      <Button onClick={() => onHandleEdit(data.map_uuid)} sx={{ textTransform: 'capitalize', fontSize: '14px', padding: '6px 12px' }}>
        Edit
      </Button>
    )
  } as unknown as ColumnData[]))

  const renderRow = (info: any) => {
    return (
      <TableRow
        key={info.map_uuid}
        sx={{ cursor: 'pointer', backgroundColor: "white", '&:hover': { backgroundColor: '#f5f5f5' } }}
        onClick={() => {
          handleSelectMap(info)
        }}
      >
        {selectable && (
          <TableCell align="center" sx={{ ...defaultTableCellStyle, width: '80px' }}>
            <Checkbox
              checked={isSelected(info)}
              onChange={() => handleSelectMap(info)}
            />
          </TableCell>
        )}
        {columns.map(column => (
          <TableCell key={column.id} align="center" sx={{ ...defaultTableCellStyle, textTransform: 'capitalize' }}>
            {info[column.id as keyof ColumnId]}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const handleChangePage = (num: number) => {
    setPage(num + 1)
  }
  const handleChangeRowsPerPage = (num: number) => {
    setRowsPerPage(num)
    setPage(1)
  }

  useEffect(() => {
    // Need to remove when actually bind data
    refetchData()
  }, [page, rowsPerPage])


  useEffect(() => {
    onUpdateData?.(maps)
  }, [maps])
  return (
    <Paper elevation={2} sx={{ display: 'flex', flexDirection: 'column', maxHeight: "67vh", borderRadius: "10px", border: "1px solid #E0E0E0", m: '20px auto' }}>
      <TableContainer sx={{ backgroundColor: "#F5F5F5", alignItems: "center", overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead sx={{ width: "100%", margin: "auto" }}>
            <TableRow sx={{ width: "100%", margin: "auto" }}>
              {selectable && (
                <TableCell width={80} align="center" sx={{ ...defaultHeaderStyle, textTransform: 'capitalize' }}>
                </TableCell>
              )}
              {columns.map(column => (
                <TableCell key={column.id} align="center" sx={renderHeader(column)}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsToShow.map(info => renderRow(info))}
            {rowsToShow.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: COLOR.gray[500] }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: COLOR.gray[500], mt: '10px' }}>No Selected Layout Maps data available</Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: COLOR.gray[500], mt: '10px' }}>Please create new layout maps</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={itemCount}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onPageChange={(e, num) => handleChangePage(num)}
        onRowsPerPageChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value || '10'))}
        sx={{ flexShrink: 0, boxShadow: "none" }} // Prevent pagination from shrinking
      />
    </Paper>
  )
}